@file:OptIn(ExperimentalResourceApi::class)

package ui.user.detail


import androidx.compose.animation.*
import androidx.compose.foundation.*
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.shape.*
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.*
import androidx.compose.ui.text.font.*
import androidx.compose.ui.unit.*
import common.frontend.api.*
import compose.assets.*
import compose.assets.svg.*
import org.jetbrains.compose.resources.*
import provider.*
import ssoft.frontend.generated.resources.*
import tk.mallumo.compose.navigation.*
import tk.mallumo.utils.*
import tools.*
import ui.user.detail.permissions.*
import ui.widget.*

data class UserArgs(var userId: Long = -1)

@ComposableNavNode(UserArgs::class)
@Composable
fun UserUI() {
    with(UserScope.rememberScope()) {
        when (val state = vm.state) {
            is DataState.Error -> ErrUI()
            is DataState.Idle,
            is DataState.Loading -> LoadingUI()

            is DataState.Result -> Content(state.nnvl)
        }

    }
}

@Composable
fun UserScope.Content(nnvl: UserScope.Wrapper) {
    val deleteState = DialogState.remember()
    val saveState = nnvl.saveState()

    DeleteDialog(deleteState)
    val toolbarActions = remember {
        buildList {
            if (nnvl.canDelete) add(ToolbarAction.delete { deleteState.show() })
            if (nnvl.canEdit) add(ToolbarAction.save(saveState) { action(UserVM.Action.Save) })
        }
    }

    Column(Modifier.fillMaxSize()) {
        if (login.canUserView()) {
            Toolbar(
                nav = nav,
                titlePrefix = nnvl.title.first,
                titleSuffix = nnvl.title.second,
                actions = toolbarActions
            )
        } else if (display.isMobile) {
            ToolbarSimple(
                titlePrefix = nnvl.title.first,
                titleSuffix = nnvl.title.second,
            ) {
                LocalHomeDrawerProvider.current.MenuIcon {}
            }
        }
        Row(Modifier.fillMaxSize()) {
            if (display.resolution.width > 900) {
                PanelLeft(nnvl, Modifier.width(384.dp).fillMaxHeight())
                if (vm.tabsScreenLarge.isNotEmpty()) {
                    Spacer(
                        Modifier.width(4.dp)
                            .fillMaxHeight()
                            .background(MaterialTheme.colorScheme.tertiaryContainer)
                    )
                    PanelRight(
                        nnvl = nnvl,
                        tabs = vm.tabsScreenLarge,
                        selection = vm.selectedTabLarge.collectAsState(),
                        modifier = Modifier.weight(1F).fillMaxHeight()
                    )
                }
            } else {
                PanelRight(
                    nnvl = nnvl,
                    tabs = vm.tabsScreenSmall,
                    selection = vm.selectedTabSmall.collectAsState(),
                    modifier = Modifier.weight(1F).fillMaxHeight()
                )
            }
        }
    }
}

@Composable
fun UserScope.DeleteDialog(deleteState: DialogState) {
    val confirmButton: @Composable () -> Unit = {
        TextButton({
            action(UserVM.Action.Delete)
            deleteState.hide()
        }) { Text(Res.string.dialog__action_delete.rememberString()) }
    }
    Dialog(deleteState) {
        DialogUI(
            Modifier.align(Alignment.Center),
            onDismissRequest = { deleteState.hide() },
            title = { Text(Res.string.user_detail__delete_title.rememberString()) },
            confirmButton = confirmButton,
            dismissButton = { TextButton({ deleteState.hide() }) { Text(Res.string.dialog__action_cancel.rememberString()) } },
        ) {
            Text(Res.string.user_detail__delete_message.rememberString())
        }
    }
}

@Composable
fun UserScope.PanelRight(
    nnvl: UserScope.Wrapper,
    modifier: Modifier,
    tabs: List<TabEntry<UserVM.TabType>>,
    selection: State<UserVM.TabType>
) {
    Column(modifier) {
        Crossfade(selection.value, Modifier.weight(1F).fillMaxWidth()) {
            when (it) {
                UserVM.TabType.PERMISSION_OWN -> {
                    PermissionItem(
                        permission = nnvl.permissionOwn,
                        canEdit = nnvl.canPermissionEditOwn
                    )
                }

                UserVM.TabType.PERMISSION_OTHER -> {
                    PermissionItem(
                        permission = nnvl.permissionOther,
                        canEdit = nnvl.canPermissionEditOther
                    )
                }

                UserVM.TabType.NONE -> Unit
                UserVM.TabType.INFO -> PanelLeft(nnvl, Modifier.fillMaxSize())
            }
        }
        if (tabs.size > 1) {
            NavigationBar(Modifier.fillMaxWidth()) {
                tabs.forEach { tabDefinition ->
                    NavigationBarItem(
                        selected = tabDefinition.tag == selection.value,
                        onClick = {
                            with(tabDefinition) { onClick(tabDefinition) }
                        },
                        label = { Text(tabDefinition.label.rememberString()) },
                        icon = { Icon(tabDefinition.icon, null) })
                }
            }
        }
    }
}

@Composable
fun UserScope.PanelLeft(nnvl: UserScope.Wrapper, modifier: Modifier) {
    val scrollState = rememberScrollState()
    Box(modifier = modifier.padding(horizontal = 8.dp)) {
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .verticalScroll(scrollState)
        ) {
            Space(8.dp)
            if (nav.parentNavigation != null) {
                CompanyItem(nnvl)
                Space(16.dp)
            }
            LoginItem(nnvl)
            Space(16.dp)
            NameItem(nnvl)
            Space(16.dp)
            ResetPasswordItem(nnvl)
            Space(16.dp)
        }
    }
}

@Composable
fun UserScope.ResetPasswordItem(nnvl: UserScope.Wrapper) {
    val state = DialogState.remember()

    Button(
        onClick = { state.show() },
        enabled = nnvl.canEdit || nnvl.data.id == login.id,
        modifier = Modifier.fillMaxWidth()
    ) {
        Icon(Svg.Lock_question, null)
        Space(8.dp)
        Text(Res.string.user_detail__pass_reset_title.rememberString(), fontWeight = FontWeight.Bold)
    }
    Dialog(state) {
        DialogUI(Modifier.align(Alignment.Center),
            onDismissRequest = { state.hide() },
            title = { Text(Res.string.user_detail__pass_reset_title.rememberString()) },
            confirmButton = {
                Button({
                    action(UserVM.Action.ResetPassword)
                    state.hide()
                }) {
                    Text(Res.string.dialog__action_reset.rememberString())
                }
            },
            dismissButton = {
                Button({ state.hide() }) {
                    Text(Res.string.dialog__action_cancel.rememberString())
                }
            }) {
            Column {
                Text(Res.string.user_detail__pass_reset_message.rememberString())
            }
        }
    }
}


@Composable
private fun CompanyItem(nnvl: UserScope.Wrapper) {
    Row(
        Modifier.fillMaxWidth()
            .padding(horizontal = 8.dp, vertical = 8.dp)
    ) {
        Icon(Svg.Store, null)
        Space(16.dp)
        Text(nnvl.data.company.name)
    }
}

@Composable
private fun LoginItem(nnvl: UserScope.Wrapper) {
    TextField(
        shape = RoundedCornerShape(topStart = 12.dp, topEnd = 12.dp),
        value = nnvl.data.login,
        readOnly = true,
        label = { Text(Res.string.user_detail__login_title.rememberString()) },
        leadingIcon = { Icon(Svg.Account, null) },
        modifier = Modifier.fillMaxWidth(),
        onValueChange = {})
}


@Composable
private fun NameItem(nnvl: UserScope.Wrapper) {
    TextField(
        shape = RoundedCornerShape(topStart = 12.dp, topEnd = 12.dp),
        value = nnvl.name.text,
        readOnly = !nnvl.canEdit,
        label = { Text(Res.string.user_detail__name_hint.rememberString()) },
        leadingIcon = { Icon(Svg.Format_text, null) },
        isError = nnvl.name.err,
        modifier = Modifier
            .fillMaxWidth(),
        onValueChange = {
            nnvl.name.set(it)
        })
}

@Composable
private fun UserScope.Wrapper.saveState(): State<Boolean> {
    val state = remember {
        mutableStateOf(false)
    }
    LaunchedEffect(change, permissionOwn.changed, permissionOther.changed) {
        state.value = change
            || (permissionOwn.changed && canPermissionEditOwn)
            || (permissionOther.changed && canPermissionEditOther)
    }

    return state
}
